<template>
	<div class="contractor-form">
		<FieldSet v-if="step" mdi-icon="lifebuoy" :label="$t('text.helpNeeded')" :info-text="$t('text.moreInfoAlturosAcademy')" />
		<FieldSet v-else :label="$t('text.yourData')" :info-text="$t('text.yourDataInfoText')" />

		<template v-if="!step || step === 'businessInformation'">
			<FieldSet class="mt-6" :label="$t('text.companyDetails')">
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="businessName"
					v-model="contractor.businessProfileData.businessName" dataCy="businessName"
					:info-text="$t('text.contractBusinessNameInfoText')"
					@update:modelValue="(value) => { contractor.businessProfileData.businessName = value; contractor.name = value; }" />
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="companyEmail"
					v-model="contractor.businessProfileData.companyEmail" dataCy="companyEmail" />
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="companyPhone"
					v-model="contractor.businessProfileData.companyPhone" dataCy="companyPhone" />

					<IfFeatureFlag flag="FEATURE_TRADE_HUB">
						<div v-if="featureEnabled(Features.TRADE_HUB)" class="logo-upload mt-4">
							<div class="logo-upload-label">
								<span>{{ $t('text.logo') }}</span>
								<p class="logo-upload-help">{{ $t('text.logoUploadHelp') }}</p>
							</div>
							<div class="logo-upload-container">
								<div class="logo-preview" v-if="logoPreview">
									<img :src="logoPreview || contractor.logoUrl" alt="Company Logo" />
									<v-btn icon size="x-small" @click="removeLogo" class="remove-logo-btn">
										<v-icon>mdi-delete</v-icon>
									</v-btn>
								</div>
								<div v-else class="upload-placeholder" @click="triggerFileInput">
									<v-icon>mdi-plus</v-icon>
								</div>
								<input
									type="file"
									ref="logoInput"
									class="logo-file-input"
									accept="image/png, image/jpeg, image/jpg"
									@change="handleLogoUpload"
									hidden
								/>
							</div>
						</div>
					</IfFeatureFlag>

					<FieldSet :label="contractor.businessProfileData.dontHaveUidNumber ? $t('text.uidNumber') : `${$t('text.uidNumber')} (${$t('text.required')})`" style="padding-left: 0;" :left-border="false" class="no-border">
						<div class="needs-uid-number">
							<div>
								<span>{{ $t('text.dontHaveUidNumber') }}</span>
								<span>{{ $t('text.dontHaveUidNumberInfoText') }}</span>
							</div>
							<v-spacer />
							<v-switch v-model="contractor.businessProfileData.dontHaveUidNumber" data-cy="dontHaveUidNumber" class="mys-switch contractorSwitch" :class="{ selected: !!contractor.businessProfileData.dontHaveUidNumber }" inset hide-details/>
						</div>
						<Field class="mt-4" typeName="BusinessProfileData" fieldName="uidNumber" :disabled="contractor.businessProfileData.dontHaveUidNumber"
							v-model="contractor.businessProfileData.uidNumber" dataCy="uidNumber" :required="!contractor.businessProfileData.dontHaveUidNumber" :watch-required="true" />
						<Field class="mt-4" typeName="BusinessProfileData" fieldName="taxId"
							v-model="contractor.businessProfileData.taxId" dataCy="taxId" />
					</FieldSet>
				<FieldSet :label="$t('text.personalInformationBusinessOwner')" :info-text="$t('text.personalInformationBusinessOwnerInfoText')" style="padding-left: 0;" :left-border="false">
					<div class="business-owner-info">
						<Field class="mt-4" typeName="BusinessProfileData" fieldName="businessOwnerDateOfBirth"
						v-model="contractor.businessProfileData.businessOwnerDateOfBirth" dataCy="businessOwnerDateOfBirth" />
						<Field class="mt-4" typeName="BusinessProfileData" fieldName="businessOwnerPlaceOfBirth"
							v-model="contractor.businessProfileData.businessOwnerPlaceOfBirth" dataCy="businessOwnerPlaceOfBirth" />
					</div>
				</FieldSet>
			</FieldSet>
			
			<FieldSet class="mt-6" :label="$t('text.companyAddress')">
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="streetAddress"
					v-model="contractor.businessProfileData.streetAddress" dataCy="streetAddress" />
				<div class="d-flex" style="gap: 10px;">
					<Field style="width: 100%;" class="mt-4" typeName="BusinessProfileData" fieldName="zipCode"
						v-model="contractor.businessProfileData.zipCode" dataCy="zipCode" />
					<Field style="width: 100%;" class="mt-4" typeName="BusinessProfileData" fieldName="city"
						v-model="contractor.businessProfileData.city" dataCy="city" />
				</div>
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="country"
					v-model="contractor.businessProfileData.country" dataCy="country" />
			</FieldSet>
		</template>
		<template v-if="!step || step === 'contractingInformation'">
			<FieldSet class="mt-6" :label="$t('text.communicationSetting')"
				:info-text="$t('text.communicationSettingInfoText')">
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="invoiceRecipientName"
					v-model="contractor.businessProfileData.invoiceRecipientName" dataCy="invoiceRecipientName" />
				<Field class="mt-4" typeName="Contractor" fieldName="email" v-model="contractor.email" dataCy="email" />
			</FieldSet>

			<FieldSet class="mt-6" :label="$t('text.bankingDetails')" :info-text="$t('text.bankingDetailsInfoText')">
				<!-- For Tour Operators, we give them the option to not input an IBAN - that's because in China IBAN is not a thing and most TOs are from China. -->
				<div v-if="isTourOperator" class="needs-uid-number">
					<div>
						<span>{{ $t('text.dontHaveIban') }}</span>
						<span>{{ $t('text.dontHaveIbanInfoText') }}</span>
					</div>
					<v-spacer />
					<v-switch v-model="contractor.businessProfileData.dontHaveIban" data-cy="dontHaveIban" class="mys-switch contractorSwitch" :class="{ selected: !!contractor.businessProfileData.dontHaveIban }" inset hide-details/>
				</div>
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="accountHolder"
					v-model="contractor.businessProfileData.accountHolder" dataCy="accountHolder" :required="!contractor.businessProfileData.dontHaveIban" :watch-required="true" />
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="iban"
					v-model="contractor.businessProfileData.iban" dataCy="iban" :required="!contractor.businessProfileData.dontHaveIban" :watch-required="true" />
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="bic"
					v-model="contractor.businessProfileData.bic" dataCy="bic" :required="!contractor.businessProfileData.dontHaveIban" :watch-required="true"  />
				<Field class="mt-4" typeName="BusinessProfileData" fieldName="creditInstitution"
					v-model="contractor.businessProfileData.creditInstitution" dataCy="creditInstitution" :required="!contractor.businessProfileData.dontHaveIban" :watch-required="true"  />
			</FieldSet>
		</template>
	</div>
</template>

<script lang="ts">
import Common from '@/mixins/Common.vue'
import Field from '@/components/fields/Field.vue'
import FieldSet from '@/views/applications/packageDesigner/FieldSet.vue'
import ChildErrorReceiver from '../../views/applications/packageDesigner/ChildErrorReceiver.vue'
import ContractExecutive from '../../../../api2/src/modules/contract/ContractExecutive'
import Toast from '@/mixins/Toast.vue'
import IfFeatureFlag from '../ifFeatureFlag/IfFeatureFlag.vue'
import { Features } from '../../../../shared/features'
import { Contractor } from '../../../../api2/src/modules/entities'

export default {
	name: 'ContractorForm',
	components: { Field, FieldSet, IfFeatureFlag },
	mixins: [Common, ChildErrorReceiver, Toast],
	props: {
		modelValue: Contractor,
		step: String,
		isTourOperator: Boolean,
	},
	data() {
		return {
			contractor: {
				businessProfileData: {}
			} as Contractor,
			logoPreview: null as null | string,
			isUploading: false,
			pendingLogoUpdate: null as null | { base64?: string; filename?: string; deleted: boolean }, // Store file data until save
			Features,
		}
	},
	watch: {
		childErrorMessage() {
			this.emitFormValidity()
		},
		contractor: {
			deep: true,
			handler(n) {
				this.$emit('update:modelValue', n)
				this.emitFormValidity()
			},
		},
	},
	mounted() {
		if (this.modelValue) {
			this.contractor = { ...this.modelValue }
		}
		if (this.contractor.logoUrl) {
			this.logoPreview = this.contractor.logoUrl
		}
		this.emitFormValidity()
	},
	methods: {
		isStepComplete() {
			const data = this.contractor?.businessProfileData ?? {}
			const hasValue = (value) => String(value ?? '').trim().length > 0

			if (this.step === 'businessInformation') {
				const requiredFields = ['businessName', 'companyEmail', 'companyPhone', 'streetAddress', 'zipCode', 'city', 'country']
				return requiredFields.every(field => hasValue(data[field]))
					&& (data.dontHaveUidNumber === true || hasValue(data.uidNumber))
			}

			if (this.step === 'contractingInformation') {
				const requiredFields = ['invoiceRecipientName']
				const bankingFields = ['accountHolder', 'iban', 'bic', 'creditInstitution']
				return requiredFields.every(field => hasValue(data[field]))
					&& hasValue(this.contractor?.email)
					&& (data.dontHaveIban === true || bankingFields.every(field => hasValue(data[field])))
			}

			return true
		},
		emitFormValidity() {
			this.$emit('update:hasFormError', !this.isStepComplete() || Boolean(this.childErrorMessage))
		},
		triggerFileInput() {
			this.$refs.logoInput.click()
		},
		async handleLogoUpload(event) {
			const file = event.target.files[0]
			if (!file) return

			// Validate file type and size (only PNG, JPG, JPEG allowed)
			const allowedTypes = ['image/png', 'image/jpeg']
			if (!allowedTypes.includes(file.type)) {
				this.showErrorToast('errorInvalidFileType')
				return
			}

			const maxSize = 10 * 1024 * 1024 // 10MB
			if (file.size > maxSize) {
				this.showErrorToast('errorFileTooLarge')
				return
			}

			try {
				// Convert file to base64 for later upload
				const base64 = await this.fileToBase64(file) as any
				
				// Store the file data for upload when saving
				this.pendingLogoUpdate = {
					base64,
					filename: file.name,
					deleted: false,
				}
				
				// Create a preview
				this.logoPreview = URL.createObjectURL(file)
				
			} catch (error) {
				console.error('Error processing logo file:', error)
				this.showErrorToast()
			}
		},
		fileToBase64(file): Promise<string> {
			return new Promise((resolve, reject) => {
				const reader = new FileReader()
				reader.readAsDataURL(file)
				reader.onload = () => resolve((reader.result as string)?.split(',')[1]) // Remove data:image/...;base64, prefix
				reader.onerror = error => reject(error)
			})
		},
		async processLogoUpdates() {
			// If there's no pending update, nothing to do
			if (this.pendingLogoUpdate === null) return

			// Delete logo if it's marked for deletion
			if (this.pendingLogoUpdate.deleted) {
				const contractExecutive = new ContractExecutive(this)
				await contractExecutive.deleteContractorLogo(
					this.contractor.id,
					this.contractor.serviceProviderId,
					this.selectedClientId
				)
				this.contractor.logoUrl = undefined
				this.pendingLogoUpdate = null

				return
			}

			// Upload new logo if there's one pending
			if (this.pendingLogoUpdate && this.pendingLogoUpdate.base64 && this.pendingLogoUpdate.filename) {
				this.isUploading = true
				const contractExecutive = new ContractExecutive(this)
				let logoUrl: string | null = null
				try {
					logoUrl = await contractExecutive.uploadContractorLogo(
						this.pendingLogoUpdate.base64,
						this.pendingLogoUpdate.filename,
						this.contractor.id,
						this.contractor.serviceProviderId,
						this.selectedClientId
					)
				} catch (error) {
					console.error('Logo upload failed:', error)
					throw error
				} finally {
					this.isUploading = false
				}
	
				// Update contractor with the logo URL from the response
				this.contractor.logoUrl = logoUrl
	
				// Clear pending upload
				this.pendingLogoUpdate = null
			}
		},
		removeLogo() {
			this.logoPreview = null

			this.pendingLogoUpdate = { deleted: true } // Mark for deletion
			if (this.$refs.logoInput) {
				this.$refs.logoInput.value = ''
			}
		}
	}
}
</script>

<style lang="scss">
.contractor-form {
	.business-owner-info {
		display: flex;
		gap: 10px;
		.Field {
			flex-grow: 1;
			margin-bottom: 0;
			.DateField input {
				max-width: none !important;
			}
		}
	}
	.logo-upload {
		margin-bottom: 16px;
		
		.logo-upload-label {
			margin-bottom: 8px;
			
			span {
				font-size: 15px;
				font-weight: 500;
				line-height: 20px;
				letter-spacing: -0.037px;
				color: black;
			}
			
			.logo-upload-help {
				font-size: 12px;
				font-style: normal;
				font-weight: 600;
				line-height: 16px;
				letter-spacing: 0.001px;
				color: #8F8F8F;
				margin-top: 4px;
				margin-bottom: 0;
			}
		}
		
		.logo-upload-container {
			display: flex;
			
			.logo-preview {
				position: relative;
				width: 100px;
				height: 100px;
				border: 1px solid #e0e0e0;
				border-radius: 4px;
				overflow: hidden;
				
				img {
					width: 100%;
					height: 100%;
					object-fit: contain;
				}
				
				.remove-logo-btn {
					position: absolute;
					top: 4px;
					right: 4px;
					// background-color: rgba(255, 255, 255, 0.7);
					background-color: rgba(255, 0, 0, 0.7);
					color: white;					
					box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
					
					&:hover {
						transform: scale(1.1);
						background-color: rgba(255, 0, 0, 0.9);
						transition: transform 0.2s ease-in-out;
					}
				}
			}
			
			.upload-placeholder {
				width: 100px;
				height: 100px;
				border: 1px dashed #8F8F8F;
				border-radius: 4px;
				display: flex;
				align-items: center;
				justify-content: center;
				cursor: pointer;
				transition: background-color 0.2s;
				
				&:hover {
					background-color: rgba(0, 174, 239, 0.05);
					border-color: #00aeef;
				}
				
				.v-icon {
					color: #8F8F8F;
					font-size: 24px;
				}
			}
		}
	}
	.needs-uid-number {
		display: flex;
		padding-top: 12px;
		.v-selection-control {
			min-height: auto;
		}
		> div {
				display: flex;
				flex-direction: column;
				justify-content: center;
			> span:first-child {
				font-size: 15px;
				font-weight: 400;
				line-height: 20px;
				letter-spacing: -0.037px;
			}
			> span:last-child {
				font-size: 12px;
				font-style: normal;
				font-weight: 600;
				line-height: 16px;
				letter-spacing: 0.001px;
				color: #8F8F8F;
			}
			.contractorSwitch.v-switch {
				.v-switch__track {
					border: 2px solid var(--status-color-info, #8F8F8F) !important;
					background: var(--primary-color-transparent, rgba(255, 255, 255, 0.00)) !important;
					opacity: 1 !important;
				}
				.v-switch__thumb {
					background-color: var(--status-color-info, #8F8F8F) !important;
					width: 24px !important;
					height: 24px !important;
					box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
				}
				&.selected {
					.v-switch__thumb {
						background-color: white !important;
					}
					.v-switch__track {
						background-color: #00aeef !important;
						border-color: #00aeef !important;
					}
				}
			}
		}
	}
	.FieldSet {
		&.no-border {
			border: none !important;
		}
		.infoText,
		>.helpText {
			font-size: 12px;
			font-weight: 400;
			line-height: 16px;
			font-style: normal;
			letter-spacing: 0.001px;
			color: black;
		}

		.FieldSet-label {
			font-size: 17px;
			font-weight: 500;
			line-height: 22px;
			letter-spacing: -0.071px;
			color: black;
		}

		.Field label.title {
			font-size: 15px;
			font-weight: 500;
			line-height: 20px;
			letter-spacing: -0.037px;
			color: black;
		}
		.Field.disabled {
			pointer-events: none;
			opacity: 0.3;
		}
	}
}
</style>
